import T from 'T'

// The arrangement is PER MODE: the read list and the batch sheet carry different
// column sets, so each keeps its own order / pinned / hidden.
export type SheetMode = 'view' | 'edit'

export type ProductivityColumnId =
  | 'unit'
  | 'material'
  | 'svcLoc'
  | 'start'
  | 'end'
  | 'break'
  | 'downtime'
  | 'lifts'
  | 'totalTons'
  | 'miles'
  | 'odometer'
  | 'odoEnd'
  | 'fuel'
  | 'fuelCost'
  | 'dispChip'
  | 'disposal'
  | 'pre'
  | 'post'
  | 'pre2'
  | 'post2'
  | 'crew'
  | 'liftsHr'
  | 'opHrs'
  | 'pl'
  | 'netMargin'
  | 'revenue'
  | 'labor'
  | 'burden'
  | 'disposalRO'

type ColumnGroup = 'operational' | 'performance' | 'financial'

export interface ProductivityColumnMeta {
  group: ColumnGroup
  label: string
  tip: string
  align: 'left' | 'right' | 'center'
  // A column absent from a mode has no width there; the dynamic clusters
  // (crew, disposal in edit) size themselves in the sheet.
  width: Partial<Record<SheetMode, number>>
  modes: ReadonlyArray<SheetMode>
  // Multi-track clusters can reorder and hide but can't freeze left.
  pinnable?: boolean
}

export const COLUMN_META: Record<ProductivityColumnId, ProductivityColumnMeta> = {
  unit: {
    group: 'operational',
    label: T.PRODUCTIVITY_COL_UNIT_NO,
    tip: T.PRODUCTIVITY_COL_UNIT_NO_TIP,
    align: 'left',
    width: { view: 110, edit: 110 },
    modes: ['view', 'edit'],
  },
  material: {
    group: 'operational',
    label: T.MATERIAL,
    tip: T.PRODUCTIVITY_COL_MATERIAL_TIP,
    align: 'left',
    width: { view: 88, edit: 88 },
    modes: ['view', 'edit'],
  },
  svcLoc: {
    group: 'operational',
    label: T.PRODUCTIVITY_COL_SVC_ZONE,
    tip: T.PRODUCTIVITY_COL_SVC_ZONE_TIP,
    align: 'left',
    width: { view: 104, edit: 104 },
    modes: ['view', 'edit'],
  },
  start: {
    group: 'operational',
    label: T.START,
    tip: T.PRODUCTIVITY_COL_START_TIP,
    align: 'left',
    width: { view: 76, edit: 76 },
    modes: ['view', 'edit'],
  },
  end: {
    group: 'operational',
    label: T.END,
    tip: T.PRODUCTIVITY_COL_END_TIP,
    align: 'left',
    width: { view: 76, edit: 76 },
    modes: ['view', 'edit'],
  },
  // Break — a run can carry several unpaid windows, but they stay ONE column in
  // both modes: a chip opening the popover, which owns the whole list. Windows
  // don't fit an inline cell, and repeating columns per window pushes the rest of
  // the sheet sideways.
  break: {
    group: 'operational',
    label: T.PRODUCTIVITY_BREAK,
    tip: T.PRODUCTIVITY_COL_BREAK_TIP,
    align: 'right',
    width: { view: 64, edit: 64 },
    modes: ['view', 'edit'],
  },
  // Downtime — non-productive minutes plus a WAM reason code. One column too: a
  // chip in the read list, minutes + reason inline in the batch sheet with the
  // popover behind the trailing affordance for extra events. Carved out of
  // working hours, so it's the "why" behind a low lifts/hr.
  downtime: {
    group: 'operational',
    label: T.PRODUCTIVITY_DOWNTIME,
    tip: T.PRODUCTIVITY_COL_DOWNTIME_TIP,
    align: 'right',
    width: { view: 96, edit: 152 },
    modes: ['view', 'edit'],
  },
  // Lifts — the day's count, summed from the per-container-size rows the
  // route-day records. A chip in both modes: the count is only correctable per
  // size, so there's nothing to key against a single inline figure.
  lifts: {
    group: 'operational',
    label: T.PRODUCTIVITY_LIFTS,
    tip: T.PRODUCTIVITY_COL_LIFTS_TIP,
    align: 'right',
    width: { view: 72, edit: 72 },
    modes: ['view', 'edit'],
  },
  totalTons: {
    group: 'operational',
    label: T.PRODUCTIVITY_COL_TONS,
    tip: T.PRODUCTIVITY_COL_TONS_TIP,
    align: 'right',
    width: { view: 60, edit: 60 },
    modes: ['view', 'edit'],
  },
  miles: {
    group: 'operational',
    label: T.PRODUCTIVITY_COL_MILES,
    tip: T.PRODUCTIVITY_COL_MILES_TIP,
    align: 'right',
    width: { view: 64, edit: 64 },
    modes: ['view', 'edit'],
  },
  odometer: {
    group: 'operational',
    label: T.PRODUCTIVITY_COL_ODO_START,
    tip: T.PRODUCTIVITY_COL_ODO_START_TIP,
    align: 'right',
    width: { view: 78, edit: 78 },
    modes: ['view', 'edit'],
  },
  odoEnd: {
    group: 'operational',
    label: T.PRODUCTIVITY_COL_ODO_END,
    tip: T.PRODUCTIVITY_COL_ODO_END_TIP,
    align: 'right',
    width: { view: 78, edit: 78 },
    modes: ['view', 'edit'],
  },
  fuel: {
    group: 'operational',
    label: T.PRODUCTIVITY_COL_FUEL,
    tip: T.PRODUCTIVITY_COL_FUEL_TIP,
    align: 'right',
    width: { view: 60, edit: 60 },
    modes: ['view', 'edit'],
  },
  // Fuel $ is the RECEIPT total, not gallons × a rate — so it's office-keyed in
  // the batch sheet rather than derived, and it's what the margin charges.
  fuelCost: {
    group: 'operational',
    label: T.PRODUCTIVITY_COL_FUEL_COST,
    tip: T.PRODUCTIVITY_COL_FUEL_COST_TIP,
    align: 'right',
    width: { view: 96, edit: 96 },
    modes: ['view', 'edit'],
  },
  dispChip: {
    group: 'operational',
    label: T.DISPOSAL,
    tip: T.PRODUCTIVITY_COL_DISP_CHIP_TIP,
    align: 'center',
    width: { view: 92 },
    modes: ['view'],
  },
  // Disposal is a DYNAMIC CLUSTER in edit-all: one column SET per ticket
  // (disposal stop · site · ticket # · arrived · departed, then N material
  // lines), with "Add ticket" appending another set. Widths and the repeated
  // sub-headers are computed in ProductivityListView, so it carries no per-mode
  // width and can't freeze left.
  disposal: {
    group: 'operational',
    label: T.DISPOSAL,
    tip: T.PRODUCTIVITY_COL_DISPOSAL_TIP,
    align: 'left',
    width: {},
    modes: ['edit'],
    pinnable: false,
  },
  // DVIR is per VEHICLE, so a run that swapped trucks carries two pairs. Pre/Post
  // are the truck the run went out on; Pre 2 / Post 2 are the one that relieved
  // it, and stay blank on the common day that never swapped — a relief truck that
  // came back without a post-trip is what these exist to surface.
  pre: {
    group: 'operational',
    label: T.PRODUCTIVITY_COL_PRE,
    tip: T.PRODUCTIVITY_COL_PRE_TIP,
    align: 'center',
    width: { view: 48, edit: 48 },
    modes: ['view', 'edit'],
  },
  post: {
    group: 'operational',
    label: T.POST,
    tip: T.PRODUCTIVITY_COL_POST_TIP,
    align: 'center',
    width: { view: 48, edit: 48 },
    modes: ['view', 'edit'],
  },
  pre2: {
    group: 'operational',
    label: T.PRODUCTIVITY_COL_PRE_RELIEF,
    tip: T.PRODUCTIVITY_COL_PRE_RELIEF_TIP,
    align: 'center',
    width: { view: 52, edit: 52 },
    modes: ['view', 'edit'],
  },
  post2: {
    group: 'operational',
    label: T.PRODUCTIVITY_COL_POST_RELIEF,
    tip: T.PRODUCTIVITY_COL_POST_RELIEF_TIP,
    align: 'center',
    width: { view: 52, edit: 52 },
    modes: ['view', 'edit'],
  },
  // Crew is one chip track in view mode and a dynamic cluster in edit-all
  // (driver + helper name/hours pairs + an add-crew control), so it can't
  // freeze left.
  crew: {
    group: 'operational',
    label: T.PRODUCTIVITY_CREW_TITLE,
    tip: T.PRODUCTIVITY_COL_CREW_TIP,
    align: 'center',
    width: { view: 96 },
    modes: ['view', 'edit'],
    pinnable: false,
  },
  liftsHr: {
    group: 'performance',
    label: T.PRODUCTIVITY_COL_LIFTS_HR,
    tip: T.PRODUCTIVITY_COL_LIFTS_HR_TIP,
    align: 'right',
    width: { view: 100, edit: 100 },
    modes: ['view', 'edit'],
  },
  opHrs: {
    group: 'performance',
    label: T.PRODUCTIVITY_COL_OP_HRS,
    tip: T.PRODUCTIVITY_COL_OP_HRS_TIP,
    align: 'right',
    width: { view: 60, edit: 60 },
    modes: ['view', 'edit'],
  },
  // `pl` keeps its id from the old "P/L" label — renaming it would discard every saved layout.
  pl: {
    group: 'financial',
    label: T.PRODUCTIVITY_COL_GROSS_MARGIN,
    tip: T.PRODUCTIVITY_COL_GROSS_MARGIN_TIP,
    align: 'right',
    width: { view: 124, edit: 124 },
    modes: ['view', 'edit'],
  },
  netMargin: {
    group: 'financial',
    label: T.PRODUCTIVITY_COL_NET_MARGIN,
    tip: T.PRODUCTIVITY_COL_NET_MARGIN_TIP,
    align: 'right',
    width: { view: 104, edit: 104 },
    modes: ['view', 'edit'],
  },
  revenue: {
    group: 'financial',
    label: T.REVENUE,
    tip: T.PRODUCTIVITY_COL_REVENUE_TIP,
    align: 'right',
    width: { view: 108, edit: 108 },
    modes: ['view', 'edit'],
  },
  // Base wages only — a Labor cell holding the burden too made the Gross Margin beside it impossible to reconcile by eye.
  labor: {
    group: 'financial',
    label: T.PRODUCTIVITY_LABOR,
    tip: T.PRODUCTIVITY_COL_LABOR_TIP,
    align: 'right',
    width: { view: 104, edit: 104 },
    modes: ['view', 'edit'],
  },
  burden: {
    group: 'financial',
    label: T.PRODUCTIVITY_BURDEN,
    tip: T.PRODUCTIVITY_COL_BURDEN_TIP,
    align: 'right',
    width: { view: 80, edit: 80 },
    modes: ['view', 'edit'],
  },
  disposalRO: {
    group: 'financial',
    label: T.DISPOSAL,
    tip: T.PRODUCTIVITY_COL_DISPOSAL_FEE_TIP,
    align: 'right',
    width: { view: 108, edit: 108 },
    modes: ['view', 'edit'],
  },
}

// Header copy for the cluster sub-columns — they aren't model columns, but they
// still need the same one-place tooltip treatment as everything else.
export const CLUSTER_TIPS = {
  dispStop: T.PRODUCTIVITY_TIP_DISP_STOP,
  dispSite: T.PRODUCTIVITY_TIP_DISP_SITE,
  ticketNo: T.PRODUCTIVITY_TIP_TICKET_NO,
  arrived: T.PRODUCTIVITY_TIP_ARRIVED,
  departed: T.PRODUCTIVITY_TIP_DEPARTED,
  dispMaterial: T.PRODUCTIVITY_TIP_DISP_MATERIAL,
  dispUom: T.PRODUCTIVITY_TIP_DISP_UOM,
  dispQty: T.PRODUCTIVITY_TIP_DISP_QTY,
  dispRate: T.PRODUCTIVITY_TIP_DISP_RATE,
  dispFee: T.PRODUCTIVITY_TIP_DISP_FEE,
  addMaterial: T.PRODUCTIVITY_TIP_ADD_MATERIAL,
  addTicket: T.PRODUCTIVITY_TIP_ADD_TICKET,
  driver: T.PRODUCTIVITY_TIP_DRIVER,
  helper: T.PRODUCTIVITY_TIP_HELPER,
  memberPay: T.PRODUCTIVITY_TIP_MEMBER_PAY,
  memberBurden: T.PRODUCTIVITY_TIP_MEMBER_BURDEN,
  memberHrs: T.PRODUCTIVITY_TIP_MEMBER_HRS,
  addCrew: T.PRODUCTIVITY_TIP_ADD_CREW,
} as const

export const ALL_COLUMN_IDS = Object.keys(COLUMN_META) as ProductivityColumnId[]

// Default column order, grouped so the sheet reads as semantic blocks rather
// than one flat run of columns: what the run WAS (identity) → who ran it
// (people) → what they drove (truck) → what they dumped (disposal) → how it went
// (performance) → what it made (financial).
//
// The two modes carry the SAME columns in the SAME blocks — edit-all just makes
// them editable. The one deliberate difference: view shows the compact disposal
// chip where edit expands the per-ticket cluster.
const IDENTITY_BLOCK: ProductivityColumnId[] = ['material', 'svcLoc']
// Within the people block, the keyable columns come first: the run window, then
// who worked it. Break and the DVIR pair are read-only cells, so leading with
// them put five dead stops between the last time field and the Driver name — the
// office tabs from Start straight through the crew instead.
const PEOPLE_BLOCK: ProductivityColumnId[] = ['start', 'end', 'crew', 'break', 'pre', 'post', 'pre2', 'post2']
const TRUCK_BLOCK: ProductivityColumnId[] = ['unit', 'miles', 'odometer', 'odoEnd', 'fuel', 'fuelCost', 'downtime']
const PERFORMANCE_BLOCK: ProductivityColumnId[] = ['liftsHr', 'opHrs']
// Inputs first, then the two results, so the eye finishes on Net Margin.
const FINANCIAL_BLOCK: ProductivityColumnId[] = ['revenue', 'labor', 'burden', 'disposalRO', 'pl', 'netMargin']

// Bumped whenever the default ORDER changes shape (not when a column is merely
// added — `normalizeArrangement` slots those in). A saved layout stamped with an
// older version is dropped so the new grouping is what the office actually sees.
export const DEFAULT_ORDER_VERSION = 4

export const DEFAULT_ORDER: Record<SheetMode, ProductivityColumnId[]> = {
  view: [...IDENTITY_BLOCK, ...PEOPLE_BLOCK, ...TRUCK_BLOCK, 'dispChip', 'lifts', 'totalTons', ...PERFORMANCE_BLOCK, ...FINANCIAL_BLOCK],
  edit: [...IDENTITY_BLOCK, ...PEOPLE_BLOCK, ...TRUCK_BLOCK, 'disposal', 'lifts', 'totalTons', ...PERFORMANCE_BLOCK, ...FINANCIAL_BLOCK],
}

export const columnsForMode = (mode: SheetMode): ProductivityColumnId[] => ALL_COLUMN_IDS.filter(id => COLUMN_META[id].modes.includes(mode))

export const isPinnable = (id: ProductivityColumnId): boolean => COLUMN_META[id].pinnable !== false

export const widthOf = (id: ProductivityColumnId, mode: SheetMode): number => COLUMN_META[id].width[mode] ?? 64

// The office's chosen layout for one mode: the full order plus which columns are
// pinned (frozen to the left, after the identity block) or hidden, and any width
// dragged out on the column header. A column absent from `widths` sizes to its
// COLUMN_META default.
export interface ModeArrangement {
  order: ProductivityColumnId[]
  pinned: ProductivityColumnId[]
  hidden: ProductivityColumnId[]
  widths: Partial<Record<ProductivityColumnId, number>>
}

// Narrower than this and the header label has nowhere to go; the grid enforces
// it too, but a persisted layout has to be clamped on the way back in.
export const MIN_COLUMN_WIDTH = 40

export type ColumnArrangement = Record<SheetMode, ModeArrangement>

export interface ProductivityColumnPreset {
  id: string
  name: string
  arrangement: ColumnArrangement
}

const modeArrangementFrom = (mode: SheetMode, pinned: ProductivityColumnId[] = []): ModeArrangement => ({
  order: [...DEFAULT_ORDER[mode]],
  pinned: pinned.filter(id => COLUMN_META[id].modes.includes(mode) && isPinnable(id)),
  hidden: [],
  widths: {},
})

export const DEFAULT_COLUMN_ARRANGEMENT: ColumnArrangement = {
  view: modeArrangementFrom('view'),
  edit: modeArrangementFrom('edit'),
}

// A layout read back out of storage: either the current per-mode shape, or the
// single-mode shape shipped before the view/edit split. Each side is partial too
// — a layout saved before column widths were persisted carries no `widths`.
export type PersistedArrangement = { view?: Partial<ModeArrangement>; edit?: Partial<ModeArrangement> } & Partial<ModeArrangement>

const normalizeMode = (side: Partial<ModeArrangement> | undefined, mode: SheetMode): ModeArrangement => {
  const valid = columnsForMode(mode)
  const seen = new Set<ProductivityColumnId>()
  const order: ProductivityColumnId[] = []
  ;(side?.order ?? []).forEach(id => {
    if (valid.includes(id) && !seen.has(id)) {
      seen.add(id)
      order.push(id)
    }
  })
  // A newly-added column absent from a saved order lands next to its
  // default-order neighbour rather than being dumped at the end (Odo end has to
  // stay beside Odo start). Walking DEFAULT_ORDER in order means a column's
  // predecessor is always placed before it.
  DEFAULT_ORDER[mode].forEach(id => {
    if (seen.has(id)) return
    const defaultIndex = DEFAULT_ORDER[mode].indexOf(id)
    const previousId = defaultIndex > 0 ? DEFAULT_ORDER[mode][defaultIndex - 1] : null
    const at = previousId ? order.indexOf(previousId) : -1
    if (at >= 0) order.splice(at + 1, 0, id)
    else order.push(id)
    seen.add(id)
  })
  const hidden = order.filter(id => (side?.hidden ?? []).includes(id))
  const pinned = order.filter(id => (side?.pinned ?? []).includes(id) && isPinnable(id) && !hidden.includes(id))
  const widths: Partial<Record<ProductivityColumnId, number>> = {}
  order.forEach(id => {
    const width = side?.widths?.[id]
    if (typeof width === 'number' && Number.isFinite(width)) widths[id] = Math.max(MIN_COLUMN_WIDTH, Math.round(width))
  })
  return { order, pinned, hidden, widths }
}

/**
 * Lets a stored layout survive the column set changing between releases: new
 * columns append beside their default neighbour and stale ids drop. A pre-split
 * layout arrives as a bare `{order,pinned,hidden}` and lifts into both modes.
 */
export const normalizeArrangement = (arrangement: PersistedArrangement | undefined | null): ColumnArrangement => {
  const legacy = arrangement && !arrangement.view && !arrangement.edit && Array.isArray(arrangement.order) ? arrangement : null
  return {
    view: normalizeMode(legacy ?? arrangement?.view, 'view'),
    edit: normalizeMode(legacy ?? arrangement?.edit, 'edit'),
  }
}

// Splits a mode's normalized arrangement into the visible columns to render,
// pinned first, hidden dropped.
export const resolveColumns = (arrangement: ColumnArrangement, mode: SheetMode): ProductivityColumnId[] => {
  const side = arrangement[mode]
  const visible = side.order.filter(id => !side.hidden.includes(id))
  const pinnedFirst = visible.filter(id => side.pinned.includes(id))
  const rest = visible.filter(id => !side.pinned.includes(id))
  return [...pinnedFirst, ...rest]
}

export interface ProductivityGridModels {
  // Every column for the mode, pinned first — hidden ones INCLUDED, because the
  // grid keeps them in `columns` and hides them through the visibility model.
  orderedIds: ProductivityColumnId[]
  hiddenIds: ProductivityColumnId[]
  pinnedIds: ProductivityColumnId[]
  widths: Partial<Record<ProductivityColumnId, number>>
}

/**
 * The arrangement in the shape the grid consumes. Pinned columns lead the order
 * as well as the pinned model: the grid derives its frozen section from column
 * position, so the two have to agree or the panel's pin order won't hold.
 */
export const resolveGridColumns = (arrangement: ColumnArrangement, mode: SheetMode): ProductivityGridModels => {
  const side = arrangement[mode]
  const pinnedIds = side.order.filter(id => side.pinned.includes(id))
  const rest = side.order.filter(id => !side.pinned.includes(id))
  return { orderedIds: [...pinnedIds, ...rest], hiddenIds: side.hidden, pinnedIds, widths: side.widths }
}

// Built-in starting points the office can apply then tweak. Each pins the
// columns its lens leads with, in both modes (prototype parity).
const presetArrangement = (pinned: ProductivityColumnId[]): ColumnArrangement => ({
  view: modeArrangementFrom('view', pinned),
  edit: modeArrangementFrom('edit', pinned),
})

export const BUILTIN_PRESETS: ReadonlyArray<ProductivityColumnPreset> = [
  { id: 'builtin-default', name: T.DEFAULT, arrangement: DEFAULT_COLUMN_ARRANGEMENT },
  {
    id: 'builtin-financial',
    name: T.PRODUCTIVITY_PRESET_FINANCIAL_FOCUS,
    // Freezing the two results is what lets the office scroll the costs against them.
    arrangement: presetArrangement(['pl', 'netMargin']),
  },
  {
    id: 'builtin-productivity',
    name: T.PRODUCTIVITY_PRESET_PRODUCTIVITY_FOCUS,
    arrangement: presetArrangement(['liftsHr', 'opHrs']),
  },
]
